import { useCallback, useMemo, useState } from 'react';

import { useTheme } from '@mui/material';

import type { UserBuyerLeadKanbanItemFragment } from '@graphql/__generated__/graphql';
import { BuyerLeadColumnTitle } from '@modules/_shared/components/buyer-lead/BuyerLeadColumnTitle';
import { BuyerLeadDeleteDialog } from '@modules/_shared/components/buyer-lead/BuyerLeadDeleteDialog';
import { KanbanColumn } from '@modules/_shared/components/kanban/KanbanColumn';
import { getStageCardColors } from '@modules/_shared/utils/leadStageSelectHelpers';
import { UserBuyerLeadKanbanItem } from '@modules/users/components/UserBuyerLeadKanbanItem';

interface UserBuyerLeadDnDColumnProps {
  index: number;
  column: {
    data: { label?: string | null; status?: string | null; id: string };
    id: string;
  };
  onItemClicked?: (item: UserBuyerLeadKanbanItemFragment) => void;
  items: UserBuyerLeadKanbanItemFragment[];
}

export const UserBuyerLeadDnDColumn: React.FC<UserBuyerLeadDnDColumnProps> = ({
  index,
  column,
  onItemClicked,
  items,
}) => {
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const { id: columnId, data: columnData } = column;
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const getItemBorderColor = useCallback(
    (item: UserBuyerLeadKanbanItemFragment) =>
      getStageCardColors(item.lead_stage?.status, palette).border,
    [palette],
  );
  const columnTitle = useMemo(
    () => (
      <BuyerLeadColumnTitle
        label={columnData.label}
        status={columnData.status}
        count={items.length}
      />
    ),
    [columnData.label, columnData.status, items],
  );

  const renderItem = useCallback(
    (data: UserBuyerLeadKanbanItemFragment) => (
      <UserBuyerLeadKanbanItem
        lead={data}
        onDelete={leadId => setLeadToDelete(leadId)}
      />
    ),
    [],
  );

  return (
    <>
      <BuyerLeadDeleteDialog
        buyerLeadId={leadToDelete}
        open={leadToDelete != null}
        onClose={() => setLeadToDelete(null)}
        onDelete={() => {
          setLeadToDelete(null);
        }}
      />
      {useMemo(
        () => (
          <KanbanColumn
            colId={columnId}
            getItemId={item => item.id}
            items={items}
            index={index}
            renderItem={renderItem}
            title={columnTitle}
            width={270}
            onItemClicked={onItemClicked}
            getItemBorderColor={getItemBorderColor}
          />
        ),
        [
          columnId,
          items,
          columnTitle,
          index,
          onItemClicked,
          renderItem,
          getItemBorderColor,
        ],
      )}
    </>
  );
};
